import { useState } from 'react';
import type { AppSession, MeView, PracticeView } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { fmtAgo, shortAddress } from '../../lib/format';
import { Avatar, Card } from '../../components/Bits';

/** The practice's doctors and clinical managers. Managers add and remove; doctors see. */
export function RosterPage({ session, me, practice, onChanged, say }: { session: AppSession; me: MeView; practice: PracticeView; onChanged: () => Promise<void>; say: (s: string) => void }) {
  const isManager = me.role === 'manager';
  const [person, setPerson] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'doctor' | 'manager'>('doctor');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setBusy(true);
    try {
      await api.addRoster({ person: person.trim(), name: name.trim(), role, ...(title.trim() ? { title: title.trim() } : {}) }, session.token);
      say(`${name.trim()} added as ${role === 'doctor' ? 'a doctor' : 'a clinical manager'}.`);
      setPerson('');
      setName('');
      setTitle('');
      await onChanged();
    } catch (e) {
      say(e instanceof ApiError ? e.message : 'Could not add them.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (personId: string, who: string) => {
    if (!confirm(`Remove ${who} from ${practice.name}?`)) return;
    try {
      await api.removeRoster(personId, session.token);
      say(`${who} removed.`);
      await onChanged();
    } catch (e) {
      say(e instanceof ApiError ? e.message : 'Could not remove them.');
    }
  };

  return (
    <>
      <header className="desk-head">
        <div>
          <h1>{isManager ? 'Roster' : 'Care team'}</h1>
          <span className="hint">
            {practice.name}
            {practice.orgName ? ` · ${practice.orgName}` : ''}
            {practice.org ? ` · ${shortAddress(practice.org)}` : ''}
          </span>
        </div>
      </header>
      <div className="two-col">
        <Card>
          <h2>People</h2>
          <ul className="roster">
            {practice.roster.map((r) => (
              <li key={r.personId}>
                <Avatar name={r.name} size="sm" />
                <span className="row-main">
                  <strong>{r.name}</strong>
                  <span className="hint">
                    {r.role === 'doctor' ? 'Doctor' : 'Clinical manager'}
                    {r.title ? ` · ${r.title}` : ''} · added {fmtAgo(r.addedAt)}
                  </span>
                  <span className="hint mono small">{r.agentName ?? r.address ?? r.personId}</span>
                </span>
                {isManager && r.personId !== session.personId ? (
                  <button type="button" className="quiet small" onClick={() => void remove(r.personId, r.name)}>
                    Remove
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
        {isManager ? (
          <Card>
            <h2>Add a doctor or manager</h2>
            <p className="hint">Name them by their Smart Agent address or agent name (for example <span className="mono">dr-shah.me</span>). When they sign in with that identity, they land on this practice.</p>
            <label className="field">
              <span>Smart Agent address or agent name</span>
              <input type="text" className="mono" value={person} placeholder="0x… or dr-shah.me" onChange={(e) => setPerson(e.target.value)} />
            </label>
            <label className="field">
              <span>Name</span>
              <input type="text" value={name} maxLength={60} placeholder="Dr. Maulik Shah" onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="field">
              <span>Title</span>
              <input type="text" value={title} maxLength={60} placeholder="MD, Cardiologist" onChange={(e) => setTitle(e.target.value)} />
            </label>
            <div className="segmented">
              <button type="button" className={role === 'doctor' ? 'on' : ''} onClick={() => setRole('doctor')}>
                Doctor
              </button>
              <button type="button" className={role === 'manager' ? 'on' : ''} onClick={() => setRole('manager')}>
                Clinical manager
              </button>
            </div>
            <button type="button" className="primary" disabled={busy || !person.trim() || !name.trim()} onClick={() => void add()}>
              {busy ? 'Adding…' : 'Add to roster'}
            </button>
          </Card>
        ) : (
          <Card>
            <h2>Managing the roster</h2>
            <p className="hint">Only a clinical manager can add or remove people. Ask {practice.roster.find((r) => r.role === 'manager')?.name ?? 'your manager'}.</p>
          </Card>
        )}
      </div>
    </>
  );
}
